import React, { useState, useEffect } from 'react';
import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useNavigate } from 'react-router-dom';
import { PencilSimpleIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import { Table, Input, Button, Modal } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

interface ProductItem {
    id: number;
    title: string;
    subtitle: string;
    image_url: string;
    point_price: number;
}

const ListProduct: React.FC = () => {
    const [data, setData] = useState<ProductItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = sessionStorage.getItem('productPage');
        return saved ? Number(saved) : 1;
    });
    const [totalItems, setTotalItems] = useState(0);
    const [count, setCount] = useState(0);
    const [searchText, setSearchText] = useState('');

    const fetchData = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            let url = `${API_URL}api/v1/be/products?page=${page}&limit=${limit}&locale=en`;
            if (searchText.trim()) url += `&title=${encodeURIComponent(searchText)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const json = await response.json();
            setData(json.data.items || []);
            setCurrentPage(json.data.current_page || 1);
            setTotalItems(json.data.total || 0);
        } catch (err) {
            console.error("Lỗi khi gọi API:", err);
        } finally {
            setLoading(false);
        }
    };

    const clickPagination = (key: number) => {
        setCurrentPage(key);
        sessionStorage.setItem('productPage', JSON.stringify(key));
    };

    useEffect(() => {
        const saved = sessionStorage.getItem('productPage');
        if (saved) setCurrentPage(Number(saved));
        else setCurrentPage(1);
    }, []);

    useEffect(() => {
        if (typeof currentPage === 'number') {
            fetchData(currentPage);
            setCount(prev => prev + 1);
        }
    }, [currentPage]);

    useEffect(() => {
        if (count > 0) setCurrentPage(1);
    }, [searchText]);

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xóa mục này không?",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await fetch(`${API_URL}api/v1/be/products/${id}`, {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    });
                    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                    const json = await response.json();
                    if (json.status) {
                        Modal.success({ title: "Thành công", content: "Xóa thành công!" });
                        await fetchData(currentPage);
                    } else {
                        Modal.error({ title: "Thất bại", content: "Xóa thất bại: " + (json.message || "Unknown error") });
                    }
                } catch (error: unknown) {
                    Modal.error({ title: "Lỗi", content: "Lỗi khi xóa: " + (error instanceof Error ? error.message : String(error)) });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const parseTitle = (raw: string) => {
        try {
            const parsed = JSON.parse(raw);
            return parsed.vi || parsed.en;
        } catch {
            return raw;
        }
    };

    const columns: ColumnsType<ProductItem> = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 70,
        },
        {
            title: 'Name',
            key: 'name',
            render: (_, item) => parseTitle(item.title),
        },
        {
            title: 'Image',
            key: 'image',
            width: 80,
            render: (_, item) => item.image_url ? (
                <img
                    src={item.image_url}
                    alt=""
                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                />
            ) : <span className="text-gray-400 text-sm">No image</span>,
        },
        {
            title: 'Subtitle',
            key: 'subtitle',
            render: (_, item) => parseTitle(item.subtitle),
        },
        {
            title: 'Point Price',
            dataIndex: 'point_price',
            width: 120,
        },
        {
            title: 'Action',
            key: 'actions',
            width: 100,
            render: (_, item) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/add-product/edit/${item.id}`)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors cursor-pointer"
                    >
                        <PencilSimpleIcon size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900 transition-colors cursor-pointer text-red-500"
                    >
                        <TrashSimpleIcon size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const handleTableChange = (pag: TablePaginationConfig) => {
        clickPagination(pag.current ?? 1);
    };

    return (
        <SidebarLayout
            title={<h4 className="text-2xl font-bold m-0! py-4">Product</h4>}
        >
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <Input.Search
                        placeholder="Search..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                        className="w-64"
                    />
                    <Button
                        type="primary"
                        className="ml-auto"
                        onClick={() => navigate('/add-product')}
                    >
                        + Add
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: 10,
                        total: totalItems,
                        showSizeChanger: false,
                        showTotal: total => `Total ${total} items`,
                    }}
                    onChange={handleTableChange}
                />
            </div>
        </SidebarLayout>
    );
};

export default ListProduct;
